import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ImageOff } from 'lucide-react';
import { Artist } from '../artistList/artist';
import { API_BASE, asArray } from '../../../config/api';
import { artistPhotoUrl, artistBackdropUrl } from '../../../config/images';
import { InlineCandidateList, PickerItem } from './InlineCandidateList';
import {
    ErrorToast,
    ToastError,
    toastErrorFromException,
    toastErrorFromResponse,
} from './ErrorToast';
import './EditModal.css';

interface Props {
    artist: Artist;
    onClose: () => void;
    onSaved: (artist: Artist) => void;
}

// ArtistCandidate as sent by GET /api/artist?id=&candidates=matches.
interface ArtistMatchCandidate {
    mbid: string;
    name: string;
    disambiguation?: string;
    type?: string;
    country?: string;
}

// artistmedia.ImageCandidate as sent by GET /api/artist?id=&candidates=art.
interface ArtistImageCandidate {
    url: string;
    source: 'fanart' | 'wikipedia';
    kind: 'photo' | 'backdrop';
}

// artisthandler.LocalImage_API as sent by GET /api/artist?id=&candidates=local-images.
interface LocalImage {
    url: string;
    kind: 'photo' | 'backdrop';
}

type Tab = 'match' | 'art' | 'refresh';

function matchCandidateSublabel(c: ArtistMatchCandidate): string {
    return [c.type, c.country, c.disambiguation].filter(Boolean).join(' · ') || 'No further detail';
}

function artCandidateLabel(c: ArtistImageCandidate): string {
    return c.source === 'fanart' ? 'Fanart.tv' : 'Wikipedia';
}

export function ArtistEditModal({ artist, onClose, onSaved }: Props): React.JSX.Element {
    const [title, setTitle] = useState(artist.getTitle());
    const [description, setDescription] = useState(artist.getDescription());
    const [origin, setOrigin] = useState(artist.getOrigin());
    const [begin, setBegin] = useState(artist.getBegin());
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const [activeTab, setActiveTab] = useState<Tab | null>(null);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerError, setPickerError] = useState<string | null>(null);
    const [matchCandidates, setMatchCandidates] = useState<ArtistMatchCandidate[]>([]);
    const [artCandidates, setArtCandidates] = useState<ArtistImageCandidate[]>([]);
    const [applyingKey, setApplyingKey] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState<string | null>(null);
    const [refreshDone, setRefreshDone] = useState(false);
    const [localImages, setLocalImages] = useState<LocalImage[]>([]);
    const [failedLocalThumbs, setFailedLocalThumbs] = useState<Set<string>>(new Set());
    // Extracted from artist.getId() so the effect dependency below is a plain
    // value React can compare. A method call in a dependency array is
    // re-evaluated on every render and cannot be statically checked, so the
    // effect silently failed to re-run when the modal was reused for a
    // different artist.
    const artistId = artist.getId();
    const [bump, setBump] = useState(0);
    // Images applied during this session, keyed by kind, so the grid can ring
    // the chosen photo and backdrop independently (they are separate groups
    // and each has its own current selection).
    const [currentArt, setCurrentArt] = useState<{ photo?: string; backdrop?: string }>({});
    // See AlbumEditModal: apply failures must not wipe the candidate list.
    const [applyError, setApplyError] = useState<ToastError | null>(null); // cache-buster after applying a new image
    const [photoOk, setPhotoOk] = useState(true);
    const [backdropOk, setBackdropOk] = useState(true);
    const [uploading, setUploading] = useState<'photo' | 'backdrop' | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const photoInputRef = useRef<HTMLInputElement>(null);
    const backdropInputRef = useRef<HTMLInputElement>(null);

    // A new bump means the underlying photo/backdrop file may have changed -
    // any previous "this image is broken" state no longer applies.
    useEffect(() => {
        setPhotoOk(true);
        setBackdropOk(true);
    }, [bump]);

    // Already-downloaded images in the artist's folder - the quick-select
    // strip under the preview. Re-listed after any apply/refresh/upload so a
    // newly saved file shows up immediately (bump also busts the browser
    // cache).
    useEffect(() => {
        setFailedLocalThumbs(new Set());
        fetch(`${API_BASE}/api/artist?id=${artistId}&candidates=local-images`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : []))
            .then((imgs) => setLocalImages(asArray<LocalImage>(imgs)))
            .catch(() => {});
    }, [artistId, bump]);

    // Force-refresh: re-fetch metadata + photos from the providers right
    // now, bypassing the scan's freshness intervals. Needs an existing
    // MusicBrainz identity - without one there's nothing to refresh from,
    // so the button stays disabled (re-identifying is the match picker).
    const refreshMetadata = async () => {
        setRefreshing(true);
        setRefreshError(null);
        setRefreshDone(false);
        try {
            const response = await fetch(`${API_BASE}/api/artist?id=${artist.getId()}&refresh=metadata`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                setRefreshError('Refresh failed');
                return;
            }
            const updated = Artist.fromJson(await response.json());
            setDescription(updated.getDescription());
            setOrigin(updated.getOrigin());
            setBegin(updated.getBegin());
            setBump((b) => b + 1);
            setRefreshDone(true);
            onSaved(updated);
        } catch {
            setRefreshError('Refresh failed');
        } finally {
            setRefreshing(false);
        }
    };

    // Selects a tab, whose content then renders inline in the panel body
    // below (rather than stacking a separate popup on top of this modal).
    // Selecting the already-active tab collapses it back to nothing.
    const selectTab = async (tab: Tab) => {
        if (activeTab === tab) {
            setActiveTab(null);
            return;
        }
        setActiveTab(tab);
        setPickerError(null);
        setApplyingKey(null);
        setUploadError(null);

        if (tab === 'refresh') {
            await refreshMetadata();
            return;
        }

        setPickerLoading(true);
        try {
            // The backend route matches on "matches"/"art", not "match"/"art" -
            // translate here rather than renaming `tab` everywhere, since
            // 'match' reads better as the internal tab value.
            const candidatesParam = tab === 'match' ? 'matches' : 'art';
            const response = await fetch(`${API_BASE}/api/artist?id=${artist.getId()}&candidates=${candidatesParam}`, {
                credentials: 'include',
            });
            if (!response.ok) {
                setPickerError('Failed to load candidates');
                return;
            }
            const result = await response.json();
            if (tab === 'match') {
                setMatchCandidates(asArray(result));
            } else {
                setArtCandidates(asArray(result));
            }
        } catch {
            setPickerError('Failed to load candidates');
        } finally {
            setPickerLoading(false);
        }
    };

    const applyMatch = async (mbid: string) => {
        setApplyingKey(mbid);
        setApplyError(null);
        try {
            const response = await fetch(
                `${API_BASE}/api/artist?id=${artist.getId()}&apply_mbid=${encodeURIComponent(mbid)}`,
                { method: 'POST', credentials: 'include' }
            );
            if (!response.ok) {
                setApplyError(await toastErrorFromResponse(response, 'Failed to apply chosen match'));
                return;
            }

            // Accepts both the wrapped ApplyResult and a plain artist payload.
            const result = await response.json();
            const artistJson = result?.artist ?? result;
            const updated = Artist.fromJson(artistJson);
            setDescription(updated.getDescription());
            setOrigin(updated.getOrigin());
            setBegin(updated.getBegin());
            onSaved(updated);
            setBump((b) => b + 1);

            if (Array.isArray(result?.skipped_steps) && result.skipped_steps.length > 0) {
                setApplyError({
                    title: 'Applied, but some details could not be fetched',
                    skippedSteps: result.skipped_steps,
                    hint: result.warning?.hint,
                    detail: result.warning?.detail,
                });
                return;
            }

            setActiveTab(null);
        } catch (err) {
            setApplyError(toastErrorFromException(err, 'Failed to apply chosen match'));
        } finally {
            setApplyingKey(null);
        }
    };

    const applyArt = async (candidate: ArtistImageCandidate) => {
        setApplyingKey(candidate.url);
        setApplyError(null);
        try {
            const params = new URLSearchParams({
                id: String(artist.getId()),
                apply_art: candidate.url,
                kind: candidate.kind,
            });
            const response = await fetch(`${API_BASE}/api/artist?${params.toString()}`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                setApplyError(await toastErrorFromResponse(response, 'Failed to apply chosen image'));
                return;
            }
            const result = await response.json();
            onSaved(Artist.fromJson(result));
            setBump((b) => b + 1);
            setCurrentArt((prev) => ({ ...prev, [candidate.kind]: candidate.url }));
            setActiveTab(null);
        } catch (err) {
            setApplyError(toastErrorFromException(err, 'Failed to apply chosen image'));
        } finally {
            setApplyingKey(null);
        }
    };

    const applyLocalImage = async (img: LocalImage) => {
        setApplyingKey(img.url);
        try {
            const response = await fetch(`${API_BASE}/api/artist?id=${artist.getId()}&apply_local=${img.kind}`, {
                method: 'POST',
                credentials: 'include',
            });
            if (response.ok) {
                onSaved(Artist.fromJson(await response.json()));
                setBump((b) => b + 1);
            }
        } finally {
            setApplyingKey(null);
        }
    };

    const uploadImage = async (kind: 'photo' | 'backdrop', file: File) => {
        setUploading(kind);
        setUploadError(null);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const response = await fetch(`${API_BASE}/api/artist/upload-image?id=${artist.getId()}&kind=${kind}`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });
            if (!response.ok) {
                const body = await response.json().catch(() => null);
                setUploadError(body?.message || 'Failed to upload image');
                return;
            }
            const result = await response.json();
            onSaved(Artist.fromJson(result));
            setBump((b) => b + 1);
        } catch {
            setUploadError('Failed to upload image');
        } finally {
            setUploading(null);
        }
    };

    const handleFileChosen = (kind: 'photo' | 'backdrop') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) uploadImage(kind, file);
    };

    const matchItems: PickerItem[] = matchCandidates.map((c) => ({
        key: c.mbid,
        label: c.name,
        sublabel: matchCandidateSublabel(c),
    }));

    const artItems: PickerItem[] = artCandidates.map((c) => ({
        key: c.url,
        thumbnailUrl: c.url,
        sublabel: artCandidateLabel(c),
        groupLabel: c.kind === 'photo' ? 'Photo' : 'Backdrop',
        selected: c.url === (c.kind === 'photo' ? currentArt.photo : currentArt.backdrop),
    }));

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) {
            setError('Title cannot be empty');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/api/artist?id=${artist.getId()}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: title.trim(), description, origin, begin }),
            });
            const result = await response.json();
            if (!response.ok) {
                setError(result.message || 'Failed to save changes');
                return;
            }
            onSaved(Artist.fromJson(result));
            onClose();
        } catch {
            setError('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div
            className="editModalOverlay"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <form className="editModalPanel" onSubmit={handleSave}>
                <button type="button" className="editModalClose" onClick={onClose} aria-label="Close">
                    <X size={16} strokeWidth={2.75} />
                </button>
                <h2 className="editModalTitle">Edit artist</h2>
                <div className="editModalImageSection">
                    <div className="editModalImagePreview">
                        {photoOk ? (
                            <img
                                src={artistPhotoUrl(artist.getId(), bump)}
                                alt=""
                                onError={() => setPhotoOk(false)}
                            />
                        ) : (
                            <div className="editModalImagePlaceholder">
                                <ImageOff size={20} strokeWidth={2} />
                            </div>
                        )}
                        <span className="editModalImageKind">Photo</span>
                    </div>
                    <div className="editModalImagePreview">
                        {backdropOk ? (
                            <img
                                src={artistBackdropUrl(artist.getId(), bump)}
                                alt=""
                                onError={() => setBackdropOk(false)}
                            />
                        ) : (
                            <div className="editModalImagePlaceholder">
                                <ImageOff size={20} strokeWidth={2} />
                            </div>
                        )}
                        <span className="editModalImageKind">Backdrop</span>
                    </div>
                    {localImages.length > 0 && (
                        <div className="editModalImageStrip">
                            {localImages.map((img) => (
                                <button
                                    key={img.url}
                                    type="button"
                                    className="editModalImageThumb"
                                    title={`Use this ${img.kind}`}
                                    disabled={applyingKey === img.url}
                                    onClick={() => applyLocalImage(img)}
                                >
                                    {!failedLocalThumbs.has(img.url) ? (
                                        <img
                                            src={`${API_BASE}${img.url}&v=${bump}`}
                                            alt=""
                                            onError={() => setFailedLocalThumbs((prev) => new Set(prev).add(img.url))}
                                        />
                                    ) : (
                                        <div className="editModalImagePlaceholder">
                                            <ImageOff size={14} strokeWidth={2} />
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="editModalFields">
                    <label className="editModalField">
                        <span className="editModalLabel">Name</span>
                        <input
                            className="editModalInput"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            maxLength={200}
                            autoFocus
                        />
                    </label>
                    <label className="editModalField">
                        <span className="editModalLabel">Bio</span>
                        <textarea
                            className="editModalTextarea"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            maxLength={2000}
                        />
                    </label>
                    <label className="editModalField">
                        <span className="editModalLabel">Origin</span>
                        <input
                            className="editModalInput"
                            type="text"
                            value={origin}
                            onChange={(e) => setOrigin(e.target.value)}
                            maxLength={200}
                        />
                    </label>
                    <label className="editModalField">
                        <span className="editModalLabel">Formed / born</span>
                        <input
                            className="editModalInput"
                            type="date"
                            value={begin}
                            onChange={(e) => setBegin(e.target.value)}
                        />
                    </label>
                    {error && <span className="editModalError">{error}</span>}
                </div>
                <div className="editModalTabRow" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'match'}
                        className={`editModalTabButton ${activeTab === 'match' ? 'editModalTabButtonActive' : ''}`}
                        onClick={() => selectTab('match')}
                    >
                        Re-identify match…
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'art'}
                        className={`editModalTabButton ${activeTab === 'art' ? 'editModalTabButtonActive' : ''}`}
                        onClick={() => selectTab('art')}
                    >
                        Choose photo/backdrop…
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'refresh'}
                        className={`editModalTabButton ${activeTab === 'refresh' ? 'editModalTabButtonActive' : ''}`}
                        onClick={() => selectTab('refresh')}
                        disabled={!artist.getMbid()}
                        title={artist.getMbid() ? 'Re-fetch bio, origin and photos now' : 'Identify this artist first (Re-identify match…)'}
                    >
                        Refresh metadata
                    </button>
                </div>
                {activeTab && (
                    <div className="editModalTabContent">
                        {/* Above the list, never instead of it - see AlbumEditModal. */}
                        <ErrorToast error={applyError} onDismiss={() => setApplyError(null)} />
                        {activeTab === 'match' && (
                            <InlineCandidateList
                                variant="list"
                                items={matchItems}
                                loading={pickerLoading}
                                error={pickerError}
                                emptyMessage="No other matches found"
                                applyingKey={applyingKey}
                                onSelect={(item) => applyMatch(item.key)}
                            />
                        )}
                        {activeTab === 'art' && (
                            <>
                                <div className="editModalUploadRow">
                                    <input
                                        ref={photoInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChosen('photo')}
                                    />
                                    <input
                                        ref={backdropInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChosen('backdrop')}
                                    />
                                    <button
                                        type="button"
                                        className="editModalUploadButton"
                                        disabled={uploading !== null}
                                        onClick={() => photoInputRef.current?.click()}
                                    >
                                        {uploading === 'photo' ? 'Uploading…' : 'Upload photo…'}
                                    </button>
                                    <button
                                        type="button"
                                        className="editModalUploadButton"
                                        disabled={uploading !== null}
                                        onClick={() => backdropInputRef.current?.click()}
                                    >
                                        {uploading === 'backdrop' ? 'Uploading…' : 'Upload backdrop…'}
                                    </button>
                                    <span className="editModalUploadHint">JPEG, PNG or WebP, up to 5MB.</span>
                                </div>
                                {uploadError && <span className="editModalError">{uploadError}</span>}
                                <InlineCandidateList
                                    variant="grid"
                                    items={artItems}
                                    loading={pickerLoading}
                                    error={pickerError}
                                    emptyMessage="No other images found"
                                    applyingKey={applyingKey}
                                    onSelect={(item) => applyArt(artCandidates.find((c) => c.url === item.key)!)}
                                />
                            </>
                        )}
                        {activeTab === 'refresh' && (
                            <div className="editModalRefreshPanel">
                                {refreshing && <span className="editModalMuted">Refreshing…</span>}
                                {!refreshing && refreshError && <span className="editModalError">{refreshError}</span>}
                                {!refreshing && !refreshError && refreshDone && (
                                    <span className="editModalMuted">Metadata refreshed.</span>
                                )}
                                <button
                                    type="button"
                                    className="editModalUploadButton"
                                    disabled={refreshing}
                                    onClick={refreshMetadata}
                                >
                                    Refresh again
                                </button>
                            </div>
                        )}
                    </div>
                )}
                <div className="editModalActions">
                    <button type="button" className="editModalCancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="editModalSave" disabled={saving}>
                        Save
                    </button>
                </div>
            </form>
        </div>,
        document.body
    );
}
